import { LatLng } from './types';

// Any GeoJSON-shaped geometry: our AreaGeometry, mapbox-gl's GeoJSON.Geometry,
// a footprint feature. Only Polygon / MultiPolygon carry rings we can test, the
// rest read as "no rings".
export type GeometryLike = { type?: string; coordinates?: unknown } | null | undefined;

// A polygon ring as [lng, lat] vertices.
export type Ring = number[][];

// Great-circle distance in metres (haversine). Shared by the add-my-place snap
// sanity check and the explore search's nearby-district ranking.
export const distanceMeters = (a: LatLng, b: LatLng): number => {
  const earthRadius = 6371000;
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * earthRadius * Math.asin(Math.sqrt(h));
};

// Ray-casting test: is [lng, lat] inside this polygon ring?
export const pointInRing = (pt: number[], ring: Ring): boolean => {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    const intersects =
      yi > pt[1] !== yj > pt[1] &&
      pt[0] < ((xj - xi) * (pt[1] - yi)) / (yj - yi) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
};

// Outer rings of a Polygon/MultiPolygon (holes ignored); [] for anything else.
export const outerRings = (geom: GeometryLike): Ring[] => {
  if (!geom || !geom.coordinates) return [];
  if (geom.type === 'Polygon') {
    const ring = (geom.coordinates as Ring[])[0];
    return ring ? [ring] : [];
  }
  if (geom.type === 'MultiPolygon') {
    return (geom.coordinates as Ring[][]).map((poly) => poly[0]).filter(Boolean);
  }
  return [];
};

// Whether a lng/lat point falls inside a Polygon/MultiPolygon (outer rings only).
// Used to confirm a building tap landed on the anchor footprint, and to find the
// district the user is standing in.
export const pointInGeometry = (pt: number[], geom: GeometryLike): boolean =>
  outerRings(geom).some((ring) => pointInRing(pt, ring));

// Metres from a point to the nearest point on the segment a-b (both [lng, lat]).
// The foot of the perpendicular is found on an equirectangular projection
// centred on the point, then measured with the haversine; at district and
// building scale the projection error is well under a percent.
export const distanceToSegment = (point: LatLng, a: number[], b: number[]): number => {
  const kx = Math.cos((point.lat * Math.PI) / 180);
  const ax = (a[0] - point.lng) * kx;
  const ay = a[1] - point.lat;
  const bx = (b[0] - point.lng) * kx;
  const by = b[1] - point.lat;
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, -(ax * dx + ay * dy) / len2));
  const fx = ax + t * dx;
  const fy = ay + t * dy;
  return distanceMeters(point, { lat: point.lat + fy, lng: point.lng + fx / kx });
};
